<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { number, object, string } from 'yup';

const { tenant, aiProductContentField, productAttributes } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    aiProductContentField: {
        type: Object,
        required: false,
    },
    productAttributes: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const attributeOptions = computed(() => {
    const options = productAttributes.map((attribute) => ({
        ...attribute,
        label: attribute.label || attribute.code,
    }));

    const currentCode = aiProductContentField?.cmsAttributeCode;

    if (
        currentCode &&
        !options.some((attribute) => attribute.code === currentCode)
    ) {
        options.unshift({ code: currentCode, label: currentCode });
    }

    return options;
});

const form = reactive({
    label: aiProductContentField ? aiProductContentField.label : '',
    cmsAttribute:
        attributeOptions.value.find(
            (attribute) =>
                attribute.code === aiProductContentField?.cmsAttributeCode,
        ) ?? null,
    maxLength: aiProductContentField ? aiProductContentField.maxLength : '',
    instructions: aiProductContentField
        ? aiProductContentField.instructions
        : '',
});

const validationSchema = object({
    label: string().required('Le libellé est requis.'),
    cmsAttribute: object().nullable().required("L'attribut CMS est requis."),
    maxLength: number()
        .nullable()
        .transform((value, original) => (original === '' ? null : value)),
    instructions: string().nullable(),
});

function backToList() {
    router.visit(
        route('front.projectManagement.ai.productContents', {
            tenant: tenant.id,
            tab: 'customFields',
        }),
    );
}

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.ai.postProductContentField', {
                tenant: tenant.id,
                ...(aiProductContentField
                    ? { aiProductContentField: aiProductContentField.id }
                    : {}),
            }),
            {
                label: form.label,
                cmsAttributeCode: form.cmsAttribute?.code,
                maxLength:
                    form.maxLength === '' ? null : Number(form.maxLength),
                instructions: form.instructions || null,
            },
        )
        .then(() => {
            toastSaved();

            backToList();
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <InputField
                    v-model="form.label"
                    label="Libellé"
                    field-name="label"
                    field-id="label"
                    required
                />

                <ComboboxField
                    v-model="form.cmsAttribute"
                    label="Attribut CMS"
                    field-name="cmsAttribute"
                    field-id="cmsAttribute"
                    filtered-option-key="label"
                    :options="attributeOptions"
                    by="code"
                    legend="Attribut produit cible côté CMS pour le push."
                    required
                >
                    <template #optionContent="{ option }">
                        {{ option.label || option.code }}
                        <span class="text-xs text-gray-400">
                            ({{ option.code }})
                        </span>
                    </template>
                </ComboboxField>

                <InputField
                    v-model="form.maxLength"
                    label="Longueur maximale"
                    field-name="maxLength"
                    field-id="maxLength"
                    type="number"
                    min="1"
                />

                <TextareaField
                    v-model="form.instructions"
                    label="Instructions de génération"
                    field-name="instructions"
                    field-id="instructions"
                    rows="6"
                    resize="vertical"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="aiProductContentField"
                    :url="
                        route(
                            'front.projectManagement.ai.deleteProductContentField',
                            {
                                tenant: tenant.id,
                                aiProductContentField: aiProductContentField.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            backToList();
                        }
                    "
                    content="Cette action supprimera la définition du champ personnalisé. Les contenus déjà générés ne seront plus poussés pour ce champ."
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
